import type { ApiWatchlist, ApiWatchlistItem, ChartSymbol, WatchlistFlagColor } from '../types/api';

export const localWatchlistStorageKey = 'app.localWatchlist';
export const localWatchlistId = 'local-watchlist';

const defaultLocalWatchlistGroups = [
  { name: 'Crypto', symbols: ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT', 'XRPUSDT'] },
  { name: 'US Stocks', symbols: ['AAPL', 'MSFT', 'NVDA', 'TSLA', 'META'] },
] as const;

export function loadLocalWatchlist(symbols: Record<string, ChartSymbol> = {}): ApiWatchlist {
  const stored = loadStoredLocalWatchlist();
  const normalized = normalizeLocalWatchlist(stored);
  if (stored && normalized.items.length > 0) return normalized;
  return { ...normalized, items: seededLocalItems(symbols) };
}

export function loadStoredLocalWatchlist(): ApiWatchlist | null {
  const stored = loadStoredWatchlist();
  if (!stored) return null;
  const normalized = normalizeLocalWatchlist(stored);
  return normalized.items.length > 0 ? normalized : null;
}

export function saveLocalWatchlist(watchlist: ApiWatchlist): ApiWatchlist {
  const normalized = normalizeLocalWatchlist(watchlist);
  localStorage.setItem(localWatchlistStorageKey, JSON.stringify(normalized));
  return normalized;
}

export function addLocalWatchlistSymbol(watchlist: ApiWatchlist, symbol: ChartSymbol): ApiWatchlist {
  if (watchlist.items.some((item) => item.item_type === 'symbol' && item.symbol === symbol.name)) return watchlist;

  return saveLocalWatchlist({
    ...watchlist,
    items: [...watchlist.items, localSymbolItem(symbol, watchlist.items.length)],
  });
}

export function addLocalWatchlistSection(watchlist: ApiWatchlist, name: string, index = watchlist.items.length): ApiWatchlist {
  const trimmed = name.trim();
  if (!trimmed) return watchlist;
  const nextItems = [...watchlist.items];
  nextItems.splice(Math.max(0, Math.min(index, nextItems.length)), 0, localSectionItem(trimmed, index));
  return saveLocalWatchlist({ ...watchlist, items: normalizeItemOrder(nextItems) });
}

export function renameLocalWatchlistItem(watchlist: ApiWatchlist, itemId: string, name: string): ApiWatchlist {
  const trimmed = name.trim();
  if (!trimmed) return watchlist;
  return saveLocalWatchlist({
    ...watchlist,
    items: watchlist.items.map((item) => item.id === itemId ? { ...item, name: trimmed } : item),
  });
}

export function setLocalWatchlistItemFlag(watchlist: ApiWatchlist, itemId: string, flagColor: WatchlistFlagColor | null): ApiWatchlist {
  return saveLocalWatchlist({
    ...watchlist,
    items: watchlist.items.map((item) => item.id === itemId ? { ...item, flag_color: flagColor } : item),
  });
}

export function deleteLocalWatchlistItem(watchlist: ApiWatchlist, itemId: string): ApiWatchlist {
  return saveLocalWatchlist({
    ...watchlist,
    items: normalizeItemOrder(watchlist.items.filter((item) => item.id !== itemId)),
  });
}

export function reorderLocalWatchlistItems(watchlist: ApiWatchlist, itemIds: string[]): ApiWatchlist {
  const byId = new Map(watchlist.items.map((item) => [item.id, item]));
  const ordered = itemIds.flatMap((id) => {
    const item = byId.get(id);
    return item ? [item] : [];
  });
  const missing = watchlist.items.filter((item) => !itemIds.includes(item.id));
  return saveLocalWatchlist({ ...watchlist, items: normalizeItemOrder([...ordered, ...missing]) });
}

function normalizeLocalWatchlist(value: ApiWatchlist | null): ApiWatchlist {
  return {
    id: localWatchlistId,
    name: value?.name || 'Watchlist',
    sort_order: 0,
    is_pinned: false,
    items: normalizeItemOrder(Array.isArray(value?.items) ? value.items : []),
  };
}

function loadStoredWatchlist(): ApiWatchlist | null {
  const raw = localStorage.getItem(localWatchlistStorageKey);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ApiWatchlist;
  } catch (err) {
    console.error(`loadLocalWatchlist(${localWatchlistStorageKey}):`, err);
    return null;
  }
}

function normalizeItemOrder(items: ApiWatchlistItem[]): ApiWatchlistItem[] {
  return items.map((item, index) => ({ ...item, sort_order: index }));
}

function seededLocalItems(symbols: Record<string, ChartSymbol>): ApiWatchlistItem[] {
  const items: ApiWatchlistItem[] = [];
  for (const group of defaultLocalWatchlistGroups) {
    items.push(localSectionItem(group.name, items.length));
    for (const symbolName of group.symbols) {
      items.push(localSymbolItem(symbols[symbolName] ?? fallbackSymbol(symbolName), items.length));
    }
  }
  return normalizeItemOrder(items);
}

function fallbackSymbol(symbol: string): ChartSymbol {
  const isStock = ['AAPL', 'MSFT', 'NVDA', 'TSLA', 'META'].includes(symbol);
  return {
    name: symbol,
    fullName: `${isStock ? 'NASDAQ' : 'Binance'}:${symbol}`,
    description: symbol,
    exchange: isStock ? 'NASDAQ' : 'Binance',
    type: isStock ? 'stock' : 'crypto',
    session: isStock ? '0930-1600' : '24x7',
    pricescale: 100,
  };
}

function localSymbolItem(symbol: ChartSymbol, sortOrder: number): ApiWatchlistItem {
  return {
    id: localId('symbol'),
    item_type: 'symbol',
    symbol: symbol.name,
    name: symbol.description || symbol.name,
    sort_order: sortOrder,
    symbol_detail: {
      symbol: symbol.name,
      name: symbol.name,
      description: symbol.description,
      exchange: symbol.exchange,
      asset_type: symbol.type,
      price_scale: symbol.pricescale,
      is_active: true,
    },
  };
}

function localSectionItem(name: string, sortOrder: number): ApiWatchlistItem {
  return {
    id: localId('section'),
    item_type: 'section',
    name,
    sort_order: sortOrder,
  };
}

function localId(prefix: string): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return `local-${prefix}-${crypto.randomUUID()}`;
  return `local-${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
